/**
 * Yakıt Yönetimi Y3 metinleri: Tüketim ekranı (ortağın 05-tuketim.jpeg; ADR 0117 §2, Ek Y3) ve
 * sefer raporu formunun defter notu. Anahtarlar API değerleriyle (operasyon, yöntem, nitelik,
 * ayrım nedeni, hata kodu) eşleşir.
 */

export const fuelConsumptionEn = {
  sourceReport: 'Source Report: {{kind}} ({{no}})',
  sourceMeta: '{{at}} (UTC) · {{vessel}}',
  openReport: 'Open Source Report',
  report: 'Report',
  reportOption: '{{no}} · {{kind}} · {{status}}',
  status: {
    draft: 'Draft',
    posted: 'Posted to ledger',
    reopened: 'Reopened',
    void: 'Void',
    not_posted: 'Before ledger',
  },
  noReport: 'No voyage report with consumption yet',
  noReportBody:
    'Consumption is entered once, in the voyage report (Noon, Departure, Arrival …). Open the next report in Voyage; its consumption is then entered here or in the report.',
  openNext: 'Open Next Report',
  info: 'Consumption Information',
  periodStart: 'Period Start',
  periodEnd: 'Period End',
  duration: 'Duration',
  hours: '{{value}} h',
  utcNote: 'Times are UTC. The period is [start, report time).',
  suggested: 'Suggested from the previous report: {{at}}',
  useSuggested: 'Use',
  operation: 'Operation',
  operations: {
    sea: 'At Sea',
    manoeuvring: 'Manoeuvring',
    port: 'In Port',
    anchor: 'At Anchor',
    drift: 'Drifting',
    canal_strait: 'Canal / Strait',
    waiting: 'Waiting',
  },
  voyage: 'Voyage',
  unassigned: 'Not matched',
  unassignedHint:
    'The report has no voyage: its consumption is in the ledger and shows as unmatched for MRV.',
  leg: 'Leg',
  cp: 'Related CP',
  derivedLater: 'Derived from the voyage summary',
  noLeg: 'No leg',
  noCp: 'No charter party',
  cpCoverage: {
    in: 'Within CP period',
    out: 'Outside CP',
    partial: 'Crosses the CP boundary',
    none: 'No CP',
  },
  details: 'Consumption Details',
  total: 'Total Consumption (MT)',
  columns: {
    no: '#',
    equipment: 'Equipment',
    fuel: 'Fuel',
    tank: 'Source Tank',
    runningHours: 'Running Hours (h)',
    method: 'Measurement Method',
    mass: 'Consumption (MT)',
    quality: 'Data Quality',
    actions: 'Actions',
  },
  methods: {
    flowmeter: 'Meter (Flowmeter)',
    tank_level: 'Tank Level Difference',
    bunker_delivery: 'Bunker Delivery',
    calculated: 'Calculated',
    estimated: 'Estimated',
  },
  qualities: { measured: 'Actual', calculated: 'Calculated', estimated: 'Estimated' },
  noLines: 'No consumption lines. Add a line for each consumer.',
  editLine: 'Edit line {{no}}',
  removeLine: 'Remove line {{no}}',
  selected: 'Selected Line Details – {{name}}',
  toggleSelected: 'Show or hide the line details',
  meterStart: 'Meter Start',
  meterEnd: 'Meter End',
  meterUnit: 'Meter Unit',
  units: { mt: 'MT', m3: 'm³', l: 'L', kg: 'kg' },
  basis: 'Calculation Basis',
  basisPlaceholder: 'End − Start',
  remarks: 'Description',
  deviceId: 'Meter ID',
  density: 'Density 15 °C (t/m³)',
  batch: 'Batch (BDN)',
  reset: 'Meter reset / rollover',
  resetAt: 'Reset at (UTC)',
  rolloverAt: 'Rollover value',
  addLine: 'Add Line',
  saveDraft: 'Save Draft',
  submit: 'Send for Approval',
  saved: 'Draft saved in the voyage report.',
  submitted: 'Report submitted; consumption posted to the ledger.',
  postedNote:
    'The report is submitted and its consumption is in the ledger. To change it, reopen the report in Voyage; on resubmission the previous lines are reversed.',
  notInLedger:
    'This report is before the ledger opening: its consumption is not posted (the opening balance already includes it).',
  noLedger:
    'The ROB ledger is not open yet: consumption is posted after the approved opening balance of the tanks.',
  voidNote: 'The report is void; its consumption was reversed in the ledger.',
  officeReadOnly:
    'Consumption is vessel data, entered on board in the voyage report; read only in the office.',
  splits: 'Period Splits',
  splitsHint:
    'Measured consumption before each boundary; the remainder is after it. There is no silent equal split.',
  addSplit: 'Add Split',
  removeSplit: 'Remove split {{no}}',
  splitAt: 'Boundary (UTC)',
  splitReason: 'Reason',
  splitReasons: {
    year: 'Year end (31 Dec 24:00 UTC)',
    port_arrival: 'Port arrival',
    fuel_change: 'Fuel change',
    regulatory_boundary: 'Regulatory boundary',
    company_change: 'Company change',
  },
  splitBasis: 'Basis',
  splitBefore: '{{fuel}} before (MT)',
  flags: {
    YEAR_SPLIT_MISSING:
      'The period crosses 31 December 24:00 UTC without a measured split: review required for MRV.',
    METER_MASS_MISMATCH: 'Line {{line}}: the MT meter difference is not the consumption.',
    ESTIMATED: 'Line {{line}} is estimated.',
  },
  formNote:
    'ROB and consumption come from the fuel ledger; they are written when the report is submitted. Enter consumption lines in Fuel Management ▸ Consumption.',
  openConsumption: 'Enter Consumption',
};

export const fuelConsumptionTr: typeof fuelConsumptionEn = {
  sourceReport: 'Kaynak Raporu: {{kind}} ({{no}})',
  sourceMeta: '{{at}} (UTC) · {{vessel}}',
  openReport: 'Kaynak Raporu Aç',
  report: 'Rapor',
  reportOption: '{{no}} · {{kind}} · {{status}}',
  status: {
    draft: 'Taslak',
    posted: 'Deftere işlendi',
    reopened: 'Yeniden açıldı',
    void: 'İptal',
    not_posted: 'Defter öncesi',
  },
  noReport: 'Henüz tüketim bölümü olan sefer raporu yok',
  noReportBody:
    'Tüketim bir kez, sefer raporunda (Noon, Departure, Arrival …) girilir. Sefer ekranında sonraki raporu açın; tüketimi burada ya da raporda girilir.',
  openNext: 'Sonraki Raporu Aç',
  info: 'Tüketim Bilgileri',
  periodStart: 'Dönem Başlangıcı',
  periodEnd: 'Dönem Bitişi',
  duration: 'Süre',
  hours: '{{value}} saat',
  utcNote: 'Saatler UTC. Dönem [başlangıç, rapor zamanı) aralığıdır.',
  suggested: 'Önceki rapordan öneri: {{at}}',
  useSuggested: 'Kullan',
  operation: 'Operasyon',
  operations: {
    sea: 'Denizde',
    manoeuvring: 'Manevra',
    port: 'Limanda',
    anchor: 'Demirde',
    drift: 'Drift',
    canal_strait: 'Kanal / Boğaz',
    waiting: 'Beklemede',
  },
  voyage: 'Voyage',
  unassigned: 'Eşleşmemiş',
  unassignedHint:
    'Raporun seferi yok: tüketimi defterde vardır, MRV için eşleşmemiş olarak görünür.',
  leg: 'Leg',
  cp: 'İlgili CP',
  derivedLater: 'Sefer özetinden türetilir',
  noLeg: 'Leg yok',
  noCp: 'Charter party yok',
  cpCoverage: {
    in: 'CP dönemi içinde',
    out: 'CP dışında',
    partial: 'CP sınırını aşıyor',
    none: 'CP yok',
  },
  details: 'Tüketim Detayları',
  total: 'Toplam Tüketim (MT)',
  columns: {
    no: '#',
    equipment: 'Ekipman',
    fuel: 'Yakıt',
    tank: 'Kaynak Tank',
    runningHours: 'Çalışma Saati (saat)',
    method: 'Ölçüm Yöntemi',
    mass: 'Tüketim (MT)',
    quality: 'Veri Niteliği',
    actions: 'İşlemler',
  },
  methods: {
    flowmeter: 'Sayaç (Flowmeter)',
    tank_level: 'Seviye Farkı',
    bunker_delivery: 'Teslim Belgesi',
    calculated: 'Hesaplanan',
    estimated: 'Tahmini',
  },
  qualities: { measured: 'Gerçek', calculated: 'Hesaplanan', estimated: 'Tahmini' },
  noLines: 'Tüketim satırı yok. Her tüketici için satır ekleyin.',
  editLine: '{{no}}. satırı düzenle',
  removeLine: '{{no}}. satırı kaldır',
  selected: 'Seçili Satır Detayları – {{name}}',
  toggleSelected: 'Satır ayrıntısını göster ya da gizle',
  meterStart: 'Sayaç Başlangıç',
  meterEnd: 'Sayaç Bitiş',
  meterUnit: 'Sayaç Birimi',
  units: { mt: 'MT', m3: 'm³', l: 'L', kg: 'kg' },
  basis: 'Hesap Dayanağı',
  basisPlaceholder: 'Bitiş − Başlangıç',
  remarks: 'Açıklama',
  deviceId: 'Sayaç No',
  density: 'Yoğunluk 15 °C (t/m³)',
  batch: 'Parti (BDN)',
  reset: 'Sayaç sıfırlama / taşma',
  resetAt: 'Sıfırlama anı (UTC)',
  rolloverAt: 'Taşma değeri',
  addLine: 'Satır Ekle',
  saveDraft: 'Taslak Kaydet',
  submit: 'Onaya Gönder',
  saved: 'Taslak sefer raporuna kaydedildi.',
  submitted: 'Rapor gönderildi; tüketim deftere işlendi.',
  postedNote:
    'Rapor gönderildi ve tüketimi defterde. Değiştirmek için raporu Sefer ekranında yeniden açın; yeniden gönderimde önceki satırlar ters kayıtla kapanır.',
  notInLedger:
    'Bu rapor defterin açılışından önce: tüketimi deftere işlenmez (açılış bakiyesi zaten içerir).',
  noLedger:
    'ROB defteri henüz açılmadı: tüketim tankların onaylı açılış bakiyesinden sonra deftere işlenir.',
  voidNote: 'Rapor iptal edildi; tüketimi defterde ters kayıtla kapandı.',
  officeReadOnly: 'Tüketim gemi verisidir, gemide sefer raporunda girilir; ofiste salt okunur.',
  splits: 'Dönem Ayrımları',
  splitsHint:
    'Her sınırdan önceki ölçülmüş tüketim; kalanı sınırdan sonradır. Sessiz eşit dağıtım yapılmaz.',
  addSplit: 'Ayrım Ekle',
  removeSplit: '{{no}}. ayrımı kaldır',
  splitAt: 'Sınır (UTC)',
  splitReason: 'Neden',
  splitReasons: {
    year: 'Yıl sonu (31 Aralık 24:00 UTC)',
    port_arrival: 'Liman varışı',
    fuel_change: 'Yakıt değişimi',
    regulatory_boundary: 'Düzenleyici sınır',
    company_change: 'Şirket devri',
  },
  splitBasis: 'Dayanak',
  splitBefore: '{{fuel}} öncesi (MT)',
  flags: {
    YEAR_SPLIT_MISSING:
      'Dönem 31 Aralık 24:00 UTC sınırını ölçülmüş ayrım olmadan geçiyor: MRV için inceleme gerekir.',
    METER_MASS_MISMATCH: '{{line}}. satır: MT sayacının farkı tüketimle aynı değil.',
    ESTIMATED: '{{line}}. satır tahmini.',
  },
  formNote:
    "ROB ve tüketim yakıt defterinden gelir; rapor gönderilince yazılır. Tüketim satırlarını Yakıt Yönetimi ▸ Tüketim'de girin.",
  openConsumption: 'Tüketimi Gir',
};

export const fuelConsumptionErrorsEn = {
  CONSUMPTION_REQUIRED:
    'The ledger is open: enter the consumption of this report (period, operation and lines) before submitting.',
  INVALID_CONSUMPTION: 'Some consumption fields are not valid.',
  CONSUMPTION_OVERLAP:
    'The period overlaps the consumption of another submitted report: the same consumption cannot be counted twice.',
  CONSUMPTION_BEFORE_OPENING:
    'The period starts before the opening balance of a tank; start it at or after the opening.',
  CONSUMPTION_NOT_APPLICABLE: 'This report type has no fuel consumption.',
  REPORT_SUBMITTED: 'The report is submitted; reopen it in Voyage to change it.',
  REPORT_VOIDED: 'The report is void.',
  CONTENT_CHANGED: 'The report changed meanwhile. Reload and try again.',
  INVALID_VOYAGE_REPORT: 'The voyage report is incomplete; complete it in Voyage.',
  NEXT_OPERATION_REQUIRED: 'Choose the next operation in the voyage report before submitting.',
};

export const fuelConsumptionErrorsTr: typeof fuelConsumptionErrorsEn = {
  CONSUMPTION_REQUIRED:
    'Defter açık: göndermeden önce raporun tüketimini (dönem, operasyon ve satırlar) girin.',
  INVALID_CONSUMPTION: 'Bazı tüketim alanları geçerli değil.',
  CONSUMPTION_OVERLAP:
    'Dönem başka bir gönderilmiş raporun tüketimiyle çakışıyor: aynı tüketim iki kez sayılamaz.',
  CONSUMPTION_BEFORE_OPENING:
    'Dönem bir tankın açılış bakiyesinden önce başlıyor; açılıştan sonra başlatın.',
  CONSUMPTION_NOT_APPLICABLE: 'Bu rapor türünde yakıt tüketimi yok.',
  REPORT_SUBMITTED: 'Rapor gönderildi; değiştirmek için Sefer ekranında yeniden açın.',
  REPORT_VOIDED: 'Rapor iptal edildi.',
  CONTENT_CHANGED: 'Rapor bu arada değişti. Yeniden yükleyip tekrar deneyin.',
  INVALID_VOYAGE_REPORT: 'Sefer raporu eksik; Sefer ekranında tamamlayın.',
  NEXT_OPERATION_REQUIRED: 'Göndermeden önce sefer raporunda sonraki işlemi seçin.',
};

export const fuelConsumptionFieldErrorsEn = {
  PERIOD_ORDER: 'Must be before the report time.',
  METER_NEGATIVE: 'The meter went back: record the reset or rollover.',
  RESET_OUTSIDE_PERIOD: 'Must be within the period.',
  SPLIT_OUTSIDE_PERIOD: 'Must be within the period.',
  SPLIT_ORDER: 'Must be after the previous split.',
  SPLIT_EXCEEDS_TOTAL: 'More than the consumption of this fuel.',
  SPLIT_FUEL_UNKNOWN: 'This fuel has no consumption line.',
  UNKNOWN_TANK: 'The tank is not on this vessel.',
  UNKNOWN_BATCH: 'The bunkering is not on this vessel.',
  INVALID_KEY: 'Not valid.',
  INVALID_DATETIME: 'Enter a date and time.',
  TOO_MANY: 'Too many lines.',
};

export const fuelConsumptionFieldErrorsTr: typeof fuelConsumptionFieldErrorsEn = {
  PERIOD_ORDER: 'Rapor zamanından önce olmalı.',
  METER_NEGATIVE: 'Sayaç geri gitmiş: sıfırlama ya da taşma kaydını girin.',
  RESET_OUTSIDE_PERIOD: 'Dönemin içinde olmalı.',
  SPLIT_OUTSIDE_PERIOD: 'Dönemin içinde olmalı.',
  SPLIT_ORDER: 'Önceki ayrımdan sonra olmalı.',
  SPLIT_EXCEEDS_TOTAL: 'Bu yakıtın tüketiminden fazla.',
  SPLIT_FUEL_UNKNOWN: 'Bu yakıtın tüketim satırı yok.',
  UNKNOWN_TANK: 'Tank bu gemide değil.',
  UNKNOWN_BATCH: 'Alım bu gemide değil.',
  INVALID_KEY: 'Geçerli değil.',
  INVALID_DATETIME: 'Tarih ve saat girin.',
  TOO_MANY: 'Satır sayısı fazla.',
};
